import React, { useState, useEffect, useMemo } from 'react';
import { fehlertext } from '../lib/fehlertext';
import { useSearchParams } from 'react-router-dom';
import { usersAPI, assignmentsAPI, jugendaemterAPI, timesheetsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Link as LinkIcon,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  ChevronsUpDown,
  Check,
  CheckCircle2,
  AlertCircle,
  Search,
  Archive,
  ArchiveRestore,
  CalendarClock,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { getMissingFields, isAssignmentComplete, isFieldMissing, erlaubteStufen } from '../lib/assignmentCompleteness';
import { useSortableData } from '../hooks/useSortableData';
import { SortableHeader } from '../components/SortableHeader';

const sortText = (v) =>
  String(v ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const GERMAN_FEDERAL_STATES = [
  "Baden-Württemberg", "Bayern", "Berlin", "Brandenburg", "Bremen",
  "Hamburg", "Hessen", "Mecklenburg-Vorpommern", "Niedersachsen",
  "Nordrhein-Westfalen", "Rheinland-Pfalz", "Saarland", "Sachsen",
  "Sachsen-Anhalt", "Schleswig-Holstein", "Thüringen"
];

const MissingChip = ({ testId }) => (
  <span
    className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700"
    data-testid={testId}
  >
    <AlertCircle className="h-3 w-3" />
    fehlt
  </span>
);

const AdminAssignments = () => {
  const [assignments, setAssignments] = useState([]);
  const [users, setUsers] = useState([]);
  const [jugendaemter, setJugendaemter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: '' });
  const [incompleteDialog, setIncompleteDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [azEdit, setAzEdit] = useState({ id: null, value: '', saving: false });
  const [deleting, setDeleting] = useState(false);
  const [employeeSearchOpen, setEmployeeSearchOpen] = useState(false);
  const [onlyIncomplete, setOnlyIncomplete] = useState(false);
  const [onlyExpiring, setOnlyExpiring] = useState(false);
  const [statusFilter, setStatusFilter] = useState('active');
  const [timesheets, setTimesheets] = useState([]);
  const [archiveDialog, setArchiveDialog] = useState({ open: false, id: null, name: '', count: 0, unsubmitted: 0 });
  const [archiving, setArchiving] = useState(false);
  const [search, setSearch] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link vom Dashboard-Hinweis: ?filter=expiring aktiviert den "Läuft bald ab"-Filter
  useEffect(() => {
    if (searchParams.get('filter') === 'expiring') {
      setOnlyExpiring(true);
    }
  }, [searchParams]);

  // Deep-link: scroll to & highlight a focused assignment from global search
  useEffect(() => {
    const focusId = searchParams.get('focus');
    if (!focusId) return;
    setOnlyIncomplete(false);
    setSearch('');
    let tries = 0;
    const tick = () => {
      const el = document.querySelector(`[data-testid="assignment-row-${focusId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-violet-500', 'ring-offset-2', 'bg-violet-50', 'transition');
        setTimeout(() => el.classList.remove('ring-2', 'ring-violet-500', 'ring-offset-2', 'bg-violet-50'), 2500);
        const next = new URLSearchParams(searchParams);
        next.delete('focus');
        setSearchParams(next, { replace: true });
        return;
      }
      if (tries++ < 15) setTimeout(tick, 200);
    };
    const t = setTimeout(tick, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  
  const [formData, setFormData] = useState({
    employee_id: '',
    child_name: '',
    school_name: '',
    weekly_hours: '',
    federal_state: '',
    jugendamt_id: '',
    qualifikation: '',
    aktenzeichen: '',
    bewilligt_bis: '',
    sick_days_limit: 20,
  });

  // Sort users alphabetically
  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const nameA = `${a.last_name} ${a.first_name}`.toLowerCase();
      const nameB = `${b.last_name} ${b.first_name}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [users]);

  const isArchived = (a) => a?.is_active === false;

  const incompleteCount = useMemo(
    () => assignments.filter((a) => !isArchived(a) && !isAssignmentComplete(a, jugendaemter)).length,
    [assignments, jugendaemter]
  );
  const formMissing = getMissingFields(formData, jugendaemter);
  const formStufen = erlaubteStufen(jugendaemter.find((j) => j.id === formData.jugendamt_id));

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [assignmentsRes, usersRes, jRes, tsRes] = await Promise.all([
        assignmentsAPI.getAll(),
        usersAPI.getAll(),
        jugendaemterAPI.getAll(),
        timesheetsAPI.getAll(),
      ]);
      setAssignments(assignmentsRes.data);
      setUsers(usersRes.data.filter(u => u.role === 'mitarbeiter'));
      setJugendaemter(jRes.data || []);
      setTimesheets(tsRes.data || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const getTimesheetStats = (assignmentId) => {
    const list = timesheets.filter((t) => t.assignment_id === assignmentId);
    const unsubmitted = list.filter((t) => t.status === 'offen').length;
    return { count: list.length, unsubmitted };
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? `${user.first_name} ${user.last_name}` : 'Unbekannt';
  };

  const getJugendamtName = (jaId) => {
    if (!jaId) return '–';
    const ja = jugendaemter.find(j => j.id === jaId);
    return ja ? ja.name : '–';
  };

  // Bewilligung: -1 abgelaufen, 0 läuft in <28 Tagen ab, 1 ok, null kein Datum
  const bewilligtStatus = (a) => {
    if (!a.bewilligt_bis) return null;
    const d = new Date(a.bewilligt_bis + 'T00:00:00');
    if (isNaN(d.getTime())) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((d - today) / 86400000);
    if (diffDays < 0) return -1;
    if (diffDays <= 28) return 0;
    return 1;
  };
  const isExpiringSoon = (a) => !isArchived(a) && [0, -1].includes(bewilligtStatus(a));
  const expiringCount = useMemo(
    () => assignments.filter(isExpiringSoon).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assignments]
  );

  const filteredAssignments = useMemo(() => {
    const q = search.trim().toLowerCase();
    return assignments.filter((a) => {
      if (statusFilter === 'active' && isArchived(a)) return false;
      if (statusFilter === 'archived' && !isArchived(a)) return false;
      if (onlyIncomplete && (isArchived(a) || isAssignmentComplete(a, jugendaemter))) return false;
      if (onlyExpiring && !isExpiringSoon(a)) return false;
      if (!q) return true;
      const hay = [
        getUserName(a.employee_id),
        a.child_name,
        a.school_name,
        a.jugendamt_id ? getJugendamtName(a.jugendamt_id) : '',
        a.qualifikation,
        a.federal_state,
        a.aktenzeichen,
      ].map((x) => String(x || '').toLowerCase()).join(' ');
      return hay.includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignments, onlyIncomplete, onlyExpiring, statusFilter, search, users, jugendaemter]);

  const sortAccessors = useMemo(() => ({
    status:        (a) => (isAssignmentComplete(a, jugendaemter) ? 1 : 0),
    mitarbeiter:   (a) => sortText(getUserName(a.employee_id)),
    kind:          (a) => sortText(a.child_name),
    schule:        (a) => sortText(a.school_name),
    stunden:       (a) => Number(a.weekly_hours) || 0,
    bundesland:    (a) => sortText(a.federal_state),
    jugendamt:     (a) => sortText(a.jugendamt_id ? getJugendamtName(a.jugendamt_id) : ''),
    qualifikation: (a) => sortText(a.qualifikation),
    aktenzeichen:  (a) => sortText(a.aktenzeichen),
    bewilligt:     (a) => a.bewilligt_bis || '9999-12-31',
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [users, jugendaemter]);

  const { sorted: visibleAssignments, sortConfig, requestSort } =
    useSortableData(filteredAssignments, sortAccessors, null);

  const filtersActive = onlyIncomplete || onlyExpiring || search.trim() !== '' || statusFilter !== 'active';

  
  const getSelectedUserName = () => {
    const user = users.find(u => u.id === formData.employee_id);
    return user ? `${user.last_name}, ${user.first_name}` : 'Mitarbeiter auswählen...';
  };

  const handleCreate = () => {
    setEditingAssignment(null);
    setFormData({
      employee_id: '',
      child_name: '',
      school_name: '',
      weekly_hours: '',
      federal_state: '',
      jugendamt_id: '',
      qualifikation: '',
      aktenzeichen: '',
      bewilligt_bis: '',
      sick_days_limit: 20,
    });
    setShowDialog(true);
  };

  const handleEdit = (assignment) => {
    setEditingAssignment(assignment);
    setFormData({
      employee_id: assignment.employee_id,
      child_name: assignment.child_name,
      school_name: assignment.school_name,
      weekly_hours: String(assignment.weekly_hours),
      federal_state: assignment.federal_state,
      jugendamt_id: assignment.jugendamt_id || '',
      qualifikation: assignment.qualifikation || '',
      aktenzeichen: assignment.aktenzeichen || '',
      bewilligt_bis: assignment.bewilligt_bis || '',
      sick_days_limit: assignment.sick_days_limit ?? 20,
    });
    setShowDialog(true);
  };

  const handleSave = async (confirmed) => {
    if (!formData.employee_id || !formData.child_name || !formData.school_name || !formData.weekly_hours) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }

    if (!confirmed && (!formData.jugendamt_id || !formData.qualifikation)) {
      setIncompleteDialog(true);
      return;
    }

    const payload = {
      ...formData,
      weekly_hours: parseFloat(formData.weekly_hours),
      sick_days_limit: parseInt(formData.sick_days_limit, 10) || 20,
      bewilligt_bis: formData.bewilligt_bis || null,
    };

    try {
      setSaving(true);
      if (editingAssignment) {
        await assignmentsAPI.update(editingAssignment.id, payload);
        toast.success('Zuordnung aktualisiert');
      } else {
        await assignmentsAPI.create(payload);
        toast.success('Zuordnung erstellt');
      }
      setShowDialog(false);
      setIncompleteDialog(false);
      fetchData();
    } catch (error) {
      toast.error(fehlertext(error, 'Fehler beim Speichern'));
    } finally {
      setSaving(false);
    }
  };

  const handleQuickSaveAktenzeichen = async (assignment) => {
    try {
      setAzEdit((prev) => ({ ...prev, saving: true }));
      await assignmentsAPI.update(assignment.id, {
        employee_id: assignment.employee_id,
        child_name: assignment.child_name,
        school_name: assignment.school_name,
        weekly_hours: assignment.weekly_hours,
        federal_state: assignment.federal_state,
        jugendamt_id: assignment.jugendamt_id || '',
        qualifikation: assignment.qualifikation || '',
        aktenzeichen: azEdit.value.trim(),
        bewilligt_bis: assignment.bewilligt_bis || null,
        sick_days_limit: assignment.sick_days_limit ?? 20,
      });
      toast.success('Aktenzeichen gespeichert');
      setAzEdit({ id: null, value: '', saving: false });
      fetchData();
    } catch (error) {
      toast.error(fehlertext(error, 'Fehler beim Speichern'));
      setAzEdit((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await assignmentsAPI.delete(deleteDialog.id);
      toast.success('Zuordnung gelöscht');
      setDeleteDialog({ open: false, id: null, name: '' });
      fetchData();
    } catch (error) {
      const detail = fehlertext(error, 'Fehler beim Löschen');
      if (error.response?.status === 409) {
        toast.error(detail, { duration: 8000 });
        setDeleteDialog({ open: false, id: null, name: '' });
      } else {
        toast.error(detail);
      }
    } finally {
      setDeleting(false);
    }
  };

  const openArchiveDialog = (assignment) => {
    const stats = getTimesheetStats(assignment.id);
    setArchiveDialog({
      open: true,
      id: assignment.id,
      name: `${assignment.child_name} (${assignment.school_name})`,
      count: stats.count,
      unsubmitted: stats.unsubmitted,
    });
  };

  const handleArchive = async () => {
    try {
      setArchiving(true);
      await assignmentsAPI.deactivate(archiveDialog.id);
      toast.success('Zuordnung archiviert');
      setArchiveDialog({ open: false, id: null, name: '', count: 0, unsubmitted: 0 });
      fetchData();
    } catch (error) {
      toast.error(fehlertext(error, 'Fehler beim Archivieren'));
    } finally {
      setArchiving(false);
    }
  };

  const handleRestore = async (assignment) => {
    try {
      await assignmentsAPI.reactivate(assignment.id);
      toast.success('Zuordnung wiederhergestellt');
      fetchData();
    } catch (error) {
      toast.error(fehlertext(error, 'Fehler beim Wiederherstellen'));
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6" data-testid="admin-assignments-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-[Manrope]">
              Zuordnungen
            </h1>
            <p className="text-slate-500 mt-1">
              Verwalten Sie Mitarbeiter-Zuordnungen zu Kindern und Schulen
            </p>
          </div>
          
          <Button
            className="bg-blue-800 hover:bg-blue-900"
            onClick={handleCreate}
            data-testid="create-assignment-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Neue Zuordnung
          </Button>
        </div>

        {/* Info Banner */}
        {users.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-amber-800">
              Erstellen Sie zunächst Mitarbeiter, bevor Sie Zuordnungen anlegen können.
            </p>
          </div>
        )}

        {/* Assignments Table */}
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <LinkIcon className="h-5 w-5" />
                Zuordnungen ({visibleAssignments.length}{filtersActive ? ` von ${assignments.length}` : ''})
              </CardTitle>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Mitarbeiter, Kind, Schule, Aktenzeichen…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-8 w-full sm:w-72"
                    data-testid="assignments-search"
                  />
                </div>
                {filtersActive && (
                  <button
                    type="button"
                    onClick={() => { setSearch(''); setOnlyIncomplete(false); setOnlyExpiring(false); setStatusFilter('active'); }}
                    className="text-sm text-blue-700 hover:underline whitespace-nowrap"
                    data-testid="assignments-filter-reset"
                  >
                    Zurücksetzen
                  </button>
                )}
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-36" data-testid="assignment-status-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Aktive</SelectItem>
                    <SelectItem value="archived">Archivierte</SelectItem>
                    <SelectItem value="all">Alle</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant={onlyIncomplete ? 'default' : 'outline'}
                  size="sm"
                  className={onlyIncomplete ? 'bg-red-600 hover:bg-red-700' : ''}
                  onClick={() => setOnlyIncomplete((v) => !v)}
                  disabled={incompleteCount === 0 && !onlyIncomplete}
                  data-testid="toggle-incomplete-filter"
                >
                  <AlertCircle className="h-4 w-4 mr-2" />
                  Nur unvollständige ({incompleteCount})
                </Button>
                <Button
                  variant={onlyExpiring ? 'default' : 'outline'}
                  size="sm"
                  className={onlyExpiring ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}
                  onClick={() => setOnlyExpiring((v) => !v)}
                  disabled={expiringCount === 0 && !onlyExpiring}
                  data-testid="expiring-filter"
                >
                  <CalendarClock className="h-4 w-4 mr-2" />
                  Läuft bald ab ({expiringCount})
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table containerClassName="max-h-[70vh]">
                <TableHeader>
                  <TableRow>
                    <SortableHeader label="Status" sortKey="status" sortConfig={sortConfig} onSort={requestSort} className="w-[130px] sticky top-0 z-20 bg-white shadow-[inset_0_-1px_0_0_#e2e8f0]" />
                    <SortableHeader label="Mitarbeiter" sortKey="mitarbeiter" sortConfig={sortConfig} onSort={requestSort} className="sticky top-0 z-20 bg-white shadow-[inset_0_-1px_0_0_#e2e8f0]" />
                    <SortableHeader label="Betreutes Kind" sortKey="kind" sortConfig={sortConfig} onSort={requestSort} className="sticky top-0 z-20 bg-white shadow-[inset_0_-1px_0_0_#e2e8f0]" />
                    <SortableHeader label="Schule" sortKey="schule" sortConfig={sortConfig} onSort={requestSort} className="sticky top-0 z-20 bg-white shadow-[inset_0_-1px_0_0_#e2e8f0]" />
                    <SortableHeader label="Std./Wo." sortKey="stunden" sortConfig={sortConfig} onSort={requestSort} align="right" className="sticky top-0 z-20 bg-white shadow-[inset_0_-1px_0_0_#e2e8f0]" />
                    <SortableHeader label={'Bundes\u00ADland'} sortKey="bundesland" sortConfig={sortConfig} onSort={requestSort} className="sticky top-0 z-20 bg-white shadow-[inset_0_-1px_0_0_#e2e8f0]" />
                    <SortableHeader label="Jugendamt" sortKey="jugendamt" sortConfig={sortConfig} onSort={requestSort} className="sticky top-0 z-20 bg-white shadow-[inset_0_-1px_0_0_#e2e8f0]" />
                    <SortableHeader label="Qual." sortKey="qualifikation" sortConfig={sortConfig} onSort={requestSort} className="sticky top-0 z-20 bg-white shadow-[inset_0_-1px_0_0_#e2e8f0]" />
                    <SortableHeader label={'Akten\u00ADzeichen'} sortKey="aktenzeichen" sortConfig={sortConfig} onSort={requestSort} className="sticky top-0 z-20 bg-white shadow-[inset_0_-1px_0_0_#e2e8f0]" />
                    <SortableHeader label={'Bewilligt\u00A0bis'} sortKey="bewilligt" sortConfig={sortConfig} onSort={requestSort} className="sticky top-0 z-20 bg-white shadow-[inset_0_-1px_0_0_#e2e8f0]" />
                    <TableHead className="text-right sticky top-0 right-0 z-30 bg-white border-l border-slate-200 shadow-[inset_0_-1px_0_0_#e2e8f0]">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleAssignments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center py-8 text-slate-500">
                        {search.trim() !== ''
                          ? `Keine Zuordnung gefunden für „${search}".`
                          : onlyIncomplete
                            ? 'Alle Zuordnungen sind vollständig.'
                            : 'Keine Zuordnungen vorhanden'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    visibleAssignments.map((assignment) => {
                      const missing = getMissingFields(assignment, jugendaemter);
                      const complete = missing.length === 0;
                      const unknownEmployee = getUserName(assignment.employee_id) === 'Unbekannt';
                      const archived = isArchived(assignment);
                      return (
                      <TableRow
                        key={assignment.id}
                        data-testid={`assignment-row-${assignment.id}`}
                        data-complete={complete ? 'true' : 'false'}
                        data-archived={archived ? 'true' : 'false'}
                        className={cn(
                          'group',
                          archived
                            ? 'bg-slate-50 hover:bg-slate-100 text-slate-400'
                            : complete ? 'bg-white hover:bg-slate-50' : 'bg-red-50 hover:bg-red-100'
                        )}
                      >
                        <TableCell className={cn('border-l-4', archived ? 'border-l-slate-300' : complete ? 'border-l-emerald-500' : 'border-l-red-500')}>
                          {archived ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex">
                                  <Badge className="gap-1 text-[10px] font-medium bg-slate-200 text-slate-600 border-slate-300 hover:bg-slate-200" data-testid={`assignment-status-${assignment.id}`} aria-label={assignment.archived_at ? `Archiviert am ${new Date(assignment.archived_at).toLocaleDateString('de-DE')}${assignment.archived_by_name ? ` von ${assignment.archived_by_name}` : ''}` : 'Archiviert'}>
                                    <Archive className="h-3 w-3" />Archiviert
                                  </Badge>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                {assignment.archived_at
                                  ? <p>Archiviert am {new Date(assignment.archived_at).toLocaleDateString('de-DE')}{assignment.archived_by_name ? ` von ${assignment.archived_by_name}` : ''}</p>
                                  : <p>Diese Zuordnung ist archiviert.</p>}
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex">
                                <Badge
                                  className={cn(
                                    'gap-1 text-[10px] font-medium',
                                    complete
                                      ? 'bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100'
                                      : 'bg-red-100 text-red-800 border-red-200 hover:bg-red-100'
                                  )}
                                  data-testid={`assignment-status-${assignment.id}`}
                                >
                                  {complete
                                    ? <><CheckCircle2 className="h-3 w-3" />Vollständig</>
                                    : <><AlertCircle className="h-3 w-3" />Unvollständig</>}
                                </Badge>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {complete
                                ? <p>Alle Angaben für die Abrechnung liegen vor.</p>
                                : <p>Es fehlt noch: {missing.join(', ')}</p>}
                            </TooltipContent>
                          </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className={cn('font-medium', unknownEmployee ? 'text-red-700' : 'text-slate-900')}>
                            {getUserName(assignment.employee_id)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">{assignment.child_name}</span>
                        </TableCell>
                        <TableCell className="text-slate-600">{assignment.school_name}</TableCell>
                        <TableCell className="text-right tabular-nums whitespace-nowrap">
                          {String(assignment.weekly_hours).replace('.', ',')}
                        </TableCell>
                        <TableCell className="text-slate-600">{assignment.federal_state}</TableCell>
                        <TableCell className="text-slate-600">
                          {assignment.jugendamt_id
                            ? getJugendamtName(assignment.jugendamt_id)
                            : <MissingChip testId={`missing-jugendamt-${assignment.id}`} />}
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {!isFieldMissing(assignment, 'qualifikation', jugendaemter)
                            ? assignment.qualifikation
                            : <MissingChip testId={`missing-qualifikation-${assignment.id}`} />}
                        </TableCell>
                        <TableCell data-testid={`assignment-aktenzeichen-${assignment.id}`}>
                          <Popover
                            open={azEdit.id === assignment.id}
                            onOpenChange={(open) =>
                              open
                                ? setAzEdit({ id: assignment.id, value: assignment.aktenzeichen || '', saving: false })
                                : setAzEdit({ id: null, value: '', saving: false })
                            }
                          >
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="text-left hover:opacity-80 cursor-pointer rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                                data-testid={`quick-edit-aktenzeichen-${assignment.id}`}
                              >
                                {assignment.aktenzeichen
                                  ? <span className="font-mono text-xs text-slate-700">{assignment.aktenzeichen}</span>
                                  : <MissingChip testId={`missing-aktenzeichen-${assignment.id}`} />}
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-72" align="start">
                              <div className="space-y-2">
                                <p className="text-sm font-medium">Aktenzeichen</p>
                                <Input
                                  autoFocus
                                  value={azEdit.value}
                                  onChange={(e) => setAzEdit((prev) => ({ ...prev, value: e.target.value }))}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') { e.preventDefault(); handleQuickSaveAktenzeichen(assignment); }
                                    if (e.key === 'Escape') { setAzEdit({ id: null, value: '', saving: false }); }
                                  }}
                                  placeholder="z.B. 51-1.2.55/K/02835/21_1 pgr"
                                  data-testid={`quick-edit-aktenzeichen-input-${assignment.id}`}
                                />
                                <p className="text-xs text-slate-500">
                                  Kann leer bleiben, wenn das Jugendamt es noch nicht geschickt hat.
                                </p>
                                <div className="flex justify-end gap-2 pt-1">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={azEdit.saving}
                                    onClick={() => setAzEdit({ id: null, value: '', saving: false })}
                                  >
                                    Abbrechen
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="bg-blue-800 hover:bg-blue-900"
                                    disabled={azEdit.saving}
                                    onClick={() => handleQuickSaveAktenzeichen(assignment)}
                                    data-testid={`quick-edit-aktenzeichen-save-${assignment.id}`}
                                  >
                                    {azEdit.saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                                    Speichern
                                  </Button>
                                </div>
                              </div>
                            </PopoverContent>
                          </Popover>
                        </TableCell>
                        <TableCell data-testid={`bewilligt-bis-cell-${assignment.id}`}>
                          {assignment.bewilligt_bis ? (() => {
                            const st = bewilligtStatus(assignment);
                            const cls = st === -1
                              ? 'bg-red-100 text-red-700 border-red-200'
                              : st === 0
                                ? 'bg-amber-100 text-amber-800 border-amber-200'
                                : 'bg-slate-50 text-slate-600 border-slate-200';
                            return (
                              <span className={cn('inline-flex items-center rounded-md border px-1.5 py-0.5 text-xs whitespace-nowrap', cls)}>
                                {new Date(assignment.bewilligt_bis + 'T00:00:00').toLocaleDateString('de-DE')}
                              </span>
                            );
                          })() : <span className="text-slate-300 text-xs">–</span>}
                        </TableCell>
                        <TableCell
                          className={cn(
                            'text-right sticky right-0 z-10 border-l border-slate-200',
                            archived
                              ? 'bg-slate-50 group-hover:bg-slate-100'
                              : complete ? 'bg-white group-hover:bg-slate-50' : 'bg-red-50 group-hover:bg-red-100'
                          )}
                        >
                          <div className="flex items-center justify-end gap-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEdit(assignment)}
                                  data-testid={`edit-assignment-${assignment.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Zuordnung bearbeiten</p>
                              </TooltipContent>
                            </Tooltip>
                            
                            {archived ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-emerald-600 hover:text-emerald-700"
                                    onClick={() => handleRestore(assignment)}
                                    data-testid={`restore-assignment-${assignment.id}`}
                                  >
                                    <ArchiveRestore className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Zuordnung wiederherstellen</p>
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-slate-600 hover:text-slate-800"
                                    onClick={() => openArchiveDialog(assignment)}
                                    data-testid={`archive-assignment-${assignment.id}`}
                                  >
                                    <Archive className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Zuordnung archivieren</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700"
                                  onClick={() => setDeleteDialog({
                                    open: true,
                                    id: assignment.id,
                                    name: `${assignment.child_name} (${assignment.school_name})`
                                  })}
                                  data-testid={`delete-assignment-${assignment.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Zuordnung löschen</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="sm:max-w-3xl max-h-[90vh] flex flex-col">
            <DialogHeader className="shrink-0">
              <DialogTitle>
                {editingAssignment ? 'Zuordnung bearbeiten' : 'Neue Zuordnung erstellen'}
              </DialogTitle>
              <DialogDescription>
                Ordnen Sie einen Mitarbeiter einem Kind und einer Schule zu
              </DialogDescription>
            </DialogHeader>

            {formMissing.length === 0 ? (
              <div className="shrink-0 flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                Alle Angaben vollständig — abrechenbar.
              </div>
            ) : (
              <div className="shrink-0 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>Es fehlt noch: {formMissing.join(', ')}. Speichern ist trotzdem möglich.</span>
              </div>
            )}

            <div className="flex-1 overflow-y-auto -mr-2 pr-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Mitarbeiter *</Label>
                <Popover open={employeeSearchOpen} onOpenChange={setEmployeeSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={employeeSearchOpen}
                      className="w-full justify-between"
                      data-testid="assignment-employee-select"
                    >
                      {getSelectedUserName()}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Mitarbeiter suchen..." />
                      <CommandList>
                        <CommandEmpty>Kein Mitarbeiter gefunden.</CommandEmpty>
                        <CommandGroup>
                          {sortedUsers.map((user) => (
                            <CommandItem
                              key={user.id}
                              value={`${user.last_name} ${user.first_name}`}
                              onSelect={() => {
                                setFormData({ ...formData, employee_id: user.id });
                                setEmployeeSearchOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  formData.employee_id === user.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {user.last_name}, {user.first_name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label htmlFor="child_name">Betreutes Kind *</Label>
                <Input
                  id="child_name"
                  value={formData.child_name}
                  onChange={(e) => setFormData({ ...formData, child_name: e.target.value })}
                  placeholder="Name des Kindes"
                  data-testid="assignment-child-name-input"
                />
              </div>

              <div>
                <Label htmlFor="school_name">Schule *</Label>
                <Input
                  id="school_name"
                  value={formData.school_name}
                  onChange={(e) => setFormData({ ...formData, school_name: e.target.value })}
                  placeholder="Name der Schule"
                  data-testid="assignment-school-name-input"
                />
              </div>

              <div>
                <Label htmlFor="weekly_hours">Wochenstunden *</Label>
                <Input
                  id="weekly_hours"
                  type="number"
                  step="0.5"
                  min="0"
                  value={formData.weekly_hours}
                  onChange={(e) => setFormData({ ...formData, weekly_hours: e.target.value })}
                  placeholder="z.B. 20"
                  data-testid="assignment-weekly-hours-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Sollstunden werden automatisch berechnet
                </p>
              </div>

              <div>
                <Label htmlFor="sick_days_limit">Bezahlte „Betr. Kind krank"-Tage / Schuljahr</Label>
                <Input
                  id="sick_days_limit"
                  type="number"
                  step="1"
                  min="0"
                  value={formData.sick_days_limit}
                  onChange={(e) => setFormData({ ...formData, sick_days_limit: e.target.value })}
                  placeholder="20"
                  data-testid="assignment-sick-days-limit-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Pro Schuljahr, Standard 20. Nur bei Ausnahmen ändern.
                </p>
              </div>

              <div>
                <Label>Bundesland *</Label>
                <Select
                  value={formData.federal_state}
                  onValueChange={(value) => setFormData({ ...formData, federal_state: value })}
                >
                  <SelectTrigger data-testid="assignment-federal-state-select" className={cn(!formData.federal_state && 'border-red-300 focus:ring-red-400')}>
                    <SelectValue placeholder="Bundesland wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {GERMAN_FEDERAL_STATES.map((state) => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Für automatische Ferienzeiten-Erkennung
                </p>
              </div>

              <div>
                <Label>Jugendamt</Label>
                <Select value={formData.jugendamt_id || ''} onValueChange={(v) => {
                  // Auftrag SQ1: passt die Stufe nicht zum neuen Amt, wird sie geleert
                  const stufen = erlaubteStufen(jugendaemter.find((j) => j.id === v));
                  const qualifikation = stufen.includes(formData.qualifikation) ? formData.qualifikation : '';
                  setFormData({ ...formData, jugendamt_id: v, qualifikation });
                }}>
                  <SelectTrigger data-testid="assignment-jugendamt-select" className={cn(!formData.jugendamt_id && 'border-red-300 focus:ring-red-400')}>
                    <SelectValue placeholder="Jugendamt wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {jugendaemter.map((j) => (<SelectItem key={j.id} value={j.id}>{j.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Qualifikation</Label>
                <Select value={formData.qualifikation || ''} onValueChange={(v) => setFormData({ ...formData, qualifikation: v })}>
                  <SelectTrigger data-testid="assignment-qualifikation-select" className={cn(!formStufen.includes(formData.qualifikation) && 'border-red-300 focus:ring-red-400')}>
                    <SelectValue placeholder="Qualifikation wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {formStufen.map((st) => (<SelectItem key={st} value={st} data-testid={`assignment-stufe-${st}`}>{st}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Aktenzeichen</Label>
                <Input
                  value={formData.aktenzeichen}
                  onChange={(e) => setFormData({ ...formData, aktenzeichen: e.target.value })}
                  placeholder="z.B. 51-1.2.55/K/02835/21_1 pgr"
                  className={cn(!formData.aktenzeichen.trim() && 'border-red-300 focus-visible:ring-red-400')}
                  data-testid="assignment-aktenzeichen-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Kann jederzeit nachgetragen werden — bis dahin ist die Zuordnung rot markiert.
                </p>
              </div>

              <div>
                <Label>Bewilligt bis</Label>
                <Input
                  type="date"
                  value={formData.bewilligt_bis || ''}
                  onChange={(e) => setFormData({ ...formData, bewilligt_bis: e.target.value })}
                  data-testid="bewilligt-bis-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Optional — leer lassen, wenn noch nicht bekannt. Läuft die Bewilligung ab, wird der Fall nach Monatsende automatisch archiviert.
                </p>
              </div>
            </div>
            </div>

            <DialogFooter className="shrink-0 border-t pt-4">
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Abbrechen
              </Button>
              <Button
                className="bg-blue-800 hover:bg-blue-900"
                onClick={function onSave() { handleSave(false); }}
                disabled={saving}
                data-testid="save-assignment-button"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                {editingAssignment ? 'Aktualisieren' : 'Erstellen'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog(prev => ({ ...prev, open }))}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Zuordnung löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Möchten Sie die Zuordnung "{deleteDialog.name}" wirklich löschen?
                Diese Aktion kann nicht rückgängig gemacht werden.
                Hinweis: Zuordnungen, zu denen bereits Stundenzettel existieren,
                können nicht gelöscht werden — sie lassen sich nur deaktivieren,
                damit die Stundenzettel-Daten (Kind, Schule, Aktenzeichen) erhalten bleiben.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700"
              >
                {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Archive Confirmation */}
        <AlertDialog open={archiveDialog.open} onOpenChange={(open) => setArchiveDialog(prev => ({ ...prev, open }))}>
          <AlertDialogContent data-testid="archive-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Zuordnung archivieren?</AlertDialogTitle>
              <AlertDialogDescription>
                „{archiveDialog.name}" wird archiviert. Der Fall verschwindet aus der Übersicht.
                Alle Stundenzettel bleiben erhalten und lassen sich jederzeit wieder herholen.
                {archiveDialog.count > 0 && (
                  <span className="mt-2 block font-medium text-slate-700">
                    Es {archiveDialog.count === 1 ? 'hängt' : 'hängen'} {archiveDialog.count} Stundenzettel daran
                    {archiveDialog.unsubmitted > 0
                      ? `, ${archiveDialog.unsubmitted === 1 ? 'einer davon ist' : `${archiveDialog.unsubmitted} davon sind`} noch nicht eingereicht.`
                      : '.'}
                  </span>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleArchive}
                disabled={archiving}
                className="bg-slate-700 hover:bg-slate-800"
                data-testid="confirm-archive-button"
              >
                {archiving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Archivieren
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={incompleteDialog} onOpenChange={setIncompleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Einsatz unvollständig</AlertDialogTitle>
              <AlertDialogDescription>
                Für diese Zuordnung fehlt noch: {formMissing.join(', ')}. Solange diese Angaben
                fehlen, kann keine Rechnung erstellt werden. Das Aktenzeichen kann jederzeit
                nachgetragen werden. Trotzdem speichern?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={function cancelIncomplete() { setIncompleteDialog(false); }}>
                Abbrechen
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={function confirmIncomplete() { handleSave(true); }}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="confirm-incomplete-save"
              >
                Trotzdem speichern
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
};

export default AdminAssignments;
